import {
  DestroyRef,
  EnvironmentInjector,
  Injectable,
  Injector,
  Signal,
  Type,
  computed,
  inject,
  signal,
} from '@angular/core'

/**
 * Both of a layout's injectors. The element injector carries the layout's own providers, its route
 * and its router-outlet contexts. The environment injector carries the providers of the layout's
 * route, such as `MergeConflictsStore`.
 *
 * A component created elsewhere needs both. Given only the element injector, it searches that
 * injector's element chain and then falls back to the environment where it was rendered, which
 * skips the layout route's providers.
 */
export interface LayoutInjectors {
  injector: Injector
  environmentInjector: EnvironmentInjector
}

/**
 * The injectors of the layout on screen, so UI above the router outlet — the global header — can
 * create components inside it.
 */
@Injectable({ providedIn: 'root' })
export class LayoutInjectorService {
  injectors = signal<LayoutInjectors | null>(null)
}

/**
 * Makes the calling component's injectors the layout injectors while that component lives. Call it
 * from a layout component's injection context.
 */
export function registerLayoutInjector(): void {
  const layoutInjector = inject(LayoutInjectorService)

  const injectors: LayoutInjectors = {
    injector: inject(Injector),
    environmentInjector: inject(EnvironmentInjector),
  }

  layoutInjector.injectors.set(injectors)

  inject(DestroyRef).onDestroy(() => {
    // A layout replacing this one may have registered already.
    if (layoutInjector.injectors() === injectors) {
      layoutInjector.injectors.set(null)
    }
  })
}

/** What `*ngComponentOutlet` needs to create a component inside the layout. */
export interface ComponentOutlet extends LayoutInjectors {
  component: Type<unknown>
}

/**
 * A component to create with the layout's injectors — the layout's stores, its route's providers
 * and its router-outlet contexts — wherever `*ngComponentOutlet` renders it. `null` while no layout
 * is registered. Call it from an injection context.
 *
 * Pass both injectors to the outlet:
 * `*ngComponentOutlet="outlet.component; injector: outlet.injector; environmentInjector: outlet.environmentInjector"`.
 *
 * The component lives where it is rendered, so it is destroyed when the renderer drops the outlet,
 * which the renderer does as soon as the layout unregisters.
 */
export function layoutComponentOutlet(component: Type<unknown>): Signal<ComponentOutlet | null> {
  const layoutInjectors = inject(LayoutInjectorService).injectors

  return computed(() => {
    const injectors = layoutInjectors()

    return injectors ? { component, ...injectors } : null
  })
}
